import { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames/bind';

import { timeFormatter } from '@autopass-web/domain';
import successMochi from '@autopass-web/domain/json/mochi-check.json';
import { MochiLottie } from '@autopass-web/mochi-component';
import { DownloadAppBar } from '@autopass-web/mochi-component';
import analyticsService from '@autopass-web/mochi-payment/services/analytics';

import styles from './index.module.scss';
import lottieStyles from './lottie.module.scss';
import { Sparkles } from './sparkles';
import successStyles from './success.module.scss';

import type { ParkingOrder } from '@autopass-web/types';
import type { ReactNode } from 'react';

const cx = classnames.bind(styles);

interface SuccessPaymentModuleProps {
  name: ParkingOrder['parkinglot_name'];
  amount: ParkingOrder['amount'];
  email: ParkingOrder['email'];
  discountAmount: ParkingOrder['discount_amount'];
  shouldLeaveBefore: ParkingOrder['should_leave_before'];
  isApsPartner: boolean;
  button?: React.ReactNode | null;
  bulletin?: React.ReactNode;
}

export const SuccessPaymentLayout = ({
  children,
  bottom,
}: {
  children: ReactNode;
  bottom?: ReactNode;
}) => {
  const [isLottieShow, setIsLottieShow] = useState<boolean>(false);
  const lottieRef = useRef(null);

  const lottieClass = cx({
    lottie: true,
    show: isLottieShow,
  });

  useEffect(() => {
    const LottiePlay = setTimeout(() => {
      setIsLottieShow(true);
      // eslint-disable-next-line
      // @ts-ignore
      lottieRef.current?.play();
    }, 900);

    return () => {
      clearTimeout(LottiePlay);
    };
  }, []);

  return (
    <div className={styles['success']}>
      <Sparkles />
      <CSSTransition appear in timeout={700} classNames={successStyles}>
        <div className={styles['card']}>
          <CSSTransition appear in timeout={1000} classNames={lottieStyles}>
            <div className={lottieClass}>
              <MochiLottie
                ref={lottieRef}
                loop={false}
                animationData={successMochi}
              />
            </div>
          </CSSTransition>
          {children}
        </div>
      </CSSTransition>
      {bottom}
    </div>
  );
};

export const SuccessPaymentModule = ({
  name,
  amount,
  email,
  discountAmount,
  shouldLeaveBefore,
  isApsPartner,
  button = null,
  bulletin,
}: SuccessPaymentModuleProps) => {
  const [isLottieShow, setIsLottieShow] = useState<boolean>(false);
  const lottieRef = useRef(null);

  const lottieClass = cx({
    lottie: true,
    show: isLottieShow,
  });

  useEffect(() => {
    const LottiePlay = setTimeout(() => {
      setIsLottieShow(true);
      // eslint-disable-next-line
      // @ts-ignore
      lottieRef.current?.play();
    }, 900);

    return () => {
      clearTimeout(LottiePlay);
    };
  }, []);

  const discountAmountNumber = Math.round(Math.abs(+discountAmount));

  return (
    <SuccessPaymentLayout
      bottom={
        !isApsPartner && (
          <CSSTransition appear in timeout={700} classNames={successStyles}>
            <div className={styles['bar']}>
              {bulletin ?? (
                <DownloadAppBar
                  onClick={() => analyticsService.successDownloadOnClick()}
                />
              )}
            </div>
          </CSSTransition>
        )
      }
    >
      <div className={styles['name']}>{name}</div>
      <div className={styles['title']}>付款完成</div>
      <div className={styles['amount']}>{Math.round(+amount)} 元</div>
      {discountAmountNumber !== 0 && (
        <div className={styles['discount']}>
          （ 折扣金額 -{discountAmountNumber} 元）
        </div>
      )}
      {!!email && (
        <div className={styles['email']}>
          消費明細將寄送至
          <br />
          {email}
        </div>
      )}
      <div className={styles['leave']}>
        請於 {timeFormatter(shouldLeaveBefore, 'day')} 前取車離場
      </div>
      {button}
    </SuccessPaymentLayout>
  );

  return (
    <div className={styles['success']}>
      <Sparkles />
      <CSSTransition appear in timeout={700} classNames={successStyles}>
        <div className={styles['card']}>
          <CSSTransition appear in timeout={1000} classNames={lottieStyles}>
            <div className={lottieClass}>
              <MochiLottie
                ref={lottieRef}
                loop={false}
                animationData={successMochi}
              />
            </div>
          </CSSTransition>
          <div className={styles['name']}>{name}</div>
          <div className={styles['title']}>付款完成</div>
          <div className={styles['amount']}>{Math.round(+amount)} 元</div>
          {discountAmountNumber !== 0 && (
            <div className={styles['discount']}>
              （ 折扣金額 -{discountAmountNumber} 元）
            </div>
          )}
          {!!email && (
            <div className={styles['email']}>
              消費明細將寄送至
              <br />
              {email}
            </div>
          )}
          <div className={styles['leave']}>
            請於 {timeFormatter(shouldLeaveBefore, 'day')} 前取車離場
          </div>
          {button}
        </div>
      </CSSTransition>
      {!isApsPartner && (
        <CSSTransition appear in timeout={700} classNames={successStyles}>
          <div className={styles['bar']}>
            {bulletin ?? (
              <DownloadAppBar
                onClick={() => analyticsService.successDownloadOnClick()}
              />
            )}
          </div>
        </CSSTransition>
      )}
    </div>
  );
};
